<template>
  <v-container grid-list-xs>
    <v-card>
      <v-card-title class="d-flex align-center flex-wrap ga-2">
        <span>Estoque do grupo</span>
        <v-spacer></v-spacer>
        <template v-if="groupSelected">
          <DialogGroupStockIn
            :user="user"
            v-model="groupInModel"
            :floatRight="true"
            @created="onGroupInCreated"
          />
          <DialogAllocateStock
            :user="user"
            v-model="allocateModel"
            :floatRight="true"
            @done="onAllocateDone"
          />
        </template>
      </v-card-title>
      <v-card-text v-if="!groupSelected">Selecione um Grupo</v-card-text>
      <template v-else>
        <v-alert type="info" variant="tonal" density="compact" class="ma-3">
          O estoque físico fica no <strong>grupo</strong>. Lojas recebem alocações temporárias
          (claim). PDV vende o mínimo entre claim da loja e disponível no grupo.
        </v-alert>

        <v-card-title class="d-flex align-center flex-wrap ga-2">
          <span>Saldos do pool</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            variant="text"
            density="compact"
            :loading="loadingBalances"
            aria-label="Atualizar saldos"
            @click="loadBalances"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="balanceHeaders"
            :items="balances"
            :loading="loadingBalances"
            :items-per-page="-1"
            return-object
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:item.product_name="{ item }">
              <span>{{ productName(item) }}</span>
            </template>
            <template v-slot:item.variant_label="{ item }">
              <span>{{ variantLabel(item) }}</span>
            </template>
            <template v-slot:item.quantity="{ value }">
              <span>{{ formatQty(value) }}</span>
            </template>
            <template v-slot:item.group_available="{ value }">
              <span>{{ formatQty(value) }}</span>
            </template>
            <template v-slot:item.reserved="{ value }">
              <span>{{ formatQty(value) }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                variant="text"
                density="compact"
                aria-label="Entrada"
                @click="startGroupIn(item)"
              >
                <v-icon color="primary">mdi-package-down</v-icon>
              </v-btn>
              <v-btn
                icon
                variant="text"
                density="compact"
                aria-label="Alocar"
                @click="startAllocate(item)"
              >
                <v-icon color="secondary">mdi-store-plus</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <div class="pa-4 text-medium-emphasis">
                Nenhum saldo no grupo. Use <strong>Entrada (compra)</strong> para registrar
                mercadoria.
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-title class="d-flex align-center flex-wrap ga-2 pt-4">
          <span>Movimentações do grupo</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            variant="text"
            density="compact"
            :loading="loadingMovements"
            aria-label="Atualizar movimentações"
            @click="loadMovements"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="movementHeaders"
            :items="movements"
            :loading="loadingMovements"
            :items-per-page="50"
            return-object
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:item.type="{ value }">
              <span>{{ movementTypeLabel(value) }}</span>
            </template>
            <template v-slot:item.product_name="{ item }">
              <span>{{ productName(item) }}</span>
            </template>
            <template v-slot:item.variant_label="{ item }">
              <span>{{ variantLabel(item) }}</span>
            </template>
            <template v-slot:item.quantity="{ value }">
              <span>{{ formatQty(value) }}</span>
            </template>
            <template v-slot:item.invoice_id="{ item }">
              <span>{{ item.invoice_id || item.invoice?.id || '—' }}</span>
            </template>
            <template v-slot:item.sale_id="{ item }">
              <span>{{ item.sale_id || item.sale?.id || '—' }}</span>
            </template>
            <template v-slot:item.store_id="{ item }">
              <span>{{ item.store_id || '—' }}</span>
            </template>
            <template v-slot:item.note="{ value }">
              <span>{{ value || '—' }}</span>
            </template>
            <template v-slot:item.created_at="{ value }">
              <span>{{ formatDateTime(value) }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </template>
    </v-card>
  </v-container>
</template>
<script>
import DialogAllocateStock from '../components/DialogAllocateStock.vue'
import DialogGroupStockIn from '../components/DialogGroupStockIn.vue'
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

const GROUP_MOVE_TYPES = {
  in: 'Compra (entrada)',
  out: 'Saída',
  sale: 'Venda',
  refund: 'Estorno',
  reserve: 'Reserva',
  release_reserve: 'Libera reserva',
  allocate: 'Alocação',
  deallocate: 'Desalocação',
  adjust: 'Ajuste',
}

export default {
  components: { DialogGroupStockIn, DialogAllocateStock },
  props: {
    user: Object,
  },
  data() {
    return {
      balances: [],
      movements: [],
      loadingBalances: false,
      loadingMovements: false,
      groupInModel: {},
      allocateModel: {},
      balanceHeaders: [
        { title: 'Produto', align: 'start', key: 'product_name', sortable: true },
        { title: 'Variação', align: 'start', key: 'variant_label', sortable: false },
        { title: 'Em mão', align: 'end', key: 'quantity', sortable: true },
        { title: 'Disponível', align: 'end', key: 'group_available', sortable: true },
        { title: 'Reservado', align: 'end', key: 'reserved', sortable: true },
        { title: 'Ações', key: 'actions', align: 'end', sortable: false },
      ],
      movementHeaders: [
        { title: 'Tipo', align: 'start', key: 'type', sortable: true },
        { title: 'Produto', align: 'start', key: 'product_name', sortable: false },
        { title: 'Variação', align: 'start', key: 'variant_label', sortable: false },
        { title: 'Qtd', align: 'end', key: 'quantity', sortable: true },
        { title: 'Loja', align: 'end', key: 'store_id', sortable: false },
        { title: 'Fatura', align: 'end', key: 'invoice_id', sortable: false },
        { title: 'Venda', align: 'end', key: 'sale_id', sortable: false },
        { title: 'Obs.', align: 'start', key: 'note', sortable: false },
        { title: 'Data', align: 'end', key: 'created_at', sortable: true },
      ],
    }
  },
  computed: {
    groupSelected() {
      return !!api.storage.selectedGroup()?.id
    },
  },
  mounted() {
    if (this.groupSelected) this.refreshAll()
  },
  activated() {
    if (this.groupSelected) this.refreshAll()
  },
  methods: {
    refreshAll() {
      this.loadBalances()
      this.loadMovements()
    },
    productName(item) {
      return item?.product?.name || `Produto #${item?.product_id || '—'}`
    },
    variantLabel(item) {
      const v = item?.variant || item?.product_variant
      if (!v) return item?.variant_label || '—'
      if (v.name_suffix) return v.name_suffix
      if (v.is_default) return 'Padrão'
      if (Array.isArray(v.options) && v.options.length) {
        return v.options.map((o) => o.value).join(' / ')
      }
      return v.id != null ? `#${v.id}` : '—'
    },
    movementTypeLabel(value) {
      return GROUP_MOVE_TYPES[value] || value || '—'
    },
    formatQty(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR')
    },
    formatDateTime(value) {
      if (!value) return '—'
      if (this.$moment) return this.$moment(value).format('DD/MM/YYYY HH:mm')
      try {
        return new Date(value).toLocaleString('pt-BR')
      } catch {
        return value
      }
    },
    loadBalances() {
      const vm = this
      if (!vm.groupSelected) return
      vm.loadingBalances = true
      return api.stock
        .listGroupBalances({ itemsPerPage: 200 })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.balances = items || []
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingBalances = false
        })
    },
    loadMovements() {
      const vm = this
      if (!vm.groupSelected) return
      vm.loadingMovements = true
      return api.stock
        .listGroupMovements({ itemsPerPage: 50 })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.movements = items || []
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingMovements = false
        })
    },
    startGroupIn(item) {
      if (!item) return
      this.groupInModel = {
        product_id: item.product_id || item.product?.id,
        product_variant_id: item.product_variant_id || item.variant?.id || null,
        product: item.product || null,
        variant: item.variant || null,
      }
    },
    startAllocate(item) {
      if (!item) return
      this.allocateModel = {
        product_id: item.product_id || item.product?.id,
        product_variant_id: item.product_variant_id || item.variant?.id || null,
        product: item.product || null,
        variant: item.variant || null,
        mode: 'allocate',
        absolute: true,
      }
    },
    onGroupInCreated() {
      this.groupInModel = {}
      this.refreshAll()
    },
    onAllocateDone() {
      this.allocateModel = {}
      this.refreshAll()
    },
  },
  watch: {
    groupSelected(newVal) {
      if (newVal) this.refreshAll()
      else {
        this.balances = []
        this.movements = []
      }
    },
  },
}
</script>
